import {useBreakPoints} from '@hconnect/uikit'
import {Button, H3, Pill, LabelRegular} from '@hm/fundamental'
import {mdiPencilOutline, mdiDeleteOutline} from '@mdi/js'
import {QueryObserverResult} from '@tanstack/react-query'
import {format, parseISO} from 'date-fns'
import React, {useState} from 'react'

import {useAdminPilotUser} from '../../../common/hooks/useAdminPilotUser'
import {useTranslation} from '../../../hooks/useTranslation'
import {prettyRoleName} from '../../../layouts/Roles/utils'
import {Permission} from '../../../modules/LoggedInUser'
import {GroupedRoleAssignment, RoleAssignment} from '../../../modules/ManageUsers.selectors'
import {DeleteRoleDialog} from '../dialogs/DeleteRoleDialog'

type RoleHeaderProps = {
  groupedRoleAssignment: GroupedRoleAssignment
  isInternal: boolean
  isCollapsed: boolean
  canEdit: boolean
  canDeleteRole: boolean
  hasRoleOrders: boolean
  onEditClick: () => void
  renderCollapsedHeaderItems: () => React.ReactNode
  renderCollapsedTitle: () => React.ReactNode
  refetchRoles: () => Promise<QueryObserverResult<RoleAssignment[] | null>>
  loggedInUserPermissions?: Permission[] | null
  isUserCreation?: boolean
}

export const RoleHeader = ({
  groupedRoleAssignment,
  isInternal,
  isCollapsed,
  canEdit,
  canDeleteRole,
  hasRoleOrders,
  onEditClick,
  renderCollapsedHeaderItems,
  renderCollapsedTitle,
  loggedInUserPermissions,
  refetchRoles,
  isUserCreation
}: RoleHeaderProps) => {
  const {t} = useTranslation()
  const isUserAdminPilot = useAdminPilotUser(loggedInUserPermissions)
  const isBusinessOwner = groupedRoleAssignment.roleType === 'BUSINESS_OWNER'
  const isDeleteRoleDisabled =
    !canDeleteRole || (!hasRoleOrders && !canEdit) || (isUserAdminPilot && isBusinessOwner)
  const [showDeleteRoleDialog, setShowDeleteRoleDialog] = useState(false)
  const screenSizes = useBreakPoints()
  const isMobile = ['xs', 'sm'].includes(screenSizes)

  return (
    <div className="w-full">
      <div className="flex justify-between w-full">
        <div className="flex gap-2 items-center">
          <Pill
            data-test-id={isInternal ? 'chip-label-internal' : 'chip-label-external'}
            type="neutral"
            variant={isInternal ? 'shade' : 'solid'}
            shape="square"
            text={t(`roleAssignment.${isInternal ? 'internal' : 'external'}`)}
          />
          {isCollapsed && renderCollapsedHeaderItems()}
          {groupedRoleAssignment.autoGenerated && (
            <Pill
              data-test-id="chip-label-auto"
              type="warning"
              shape="square"
              text={t('roleAssignment.autoGenerated')}
            />
          )}
          {!hasRoleOrders && !canEdit && (
            <Pill
              data-test-id="chip-label-auto"
              type="warning"
              shape="square"
              text={t('roleAssignment.notEditable')}
            />
          )}
        </div>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            icon={mdiPencilOutline}
            type="button"
            onClick={onEditClick}
            data-cy={`edit-role-button-${groupedRoleAssignment.groupId}`}
            disabled={!canEdit}
          >
            {t('roleAssignment.edit')}
          </Button>
          <Button
            variant="secondary"
            data-test-id={`delete-role-button-${groupedRoleAssignment.groupId}`}
            icon={mdiDeleteOutline}
            type="button"
            disabled={isDeleteRoleDisabled}
            onClick={() => setShowDeleteRoleDialog(true)}
            className="px-3"
            data-cy={`delete-role-button-${groupedRoleAssignment.groupId}`}
          />
        </div>
      </div>

      <div className="flex">
        <H3>
          {t(
            `roleAssignment.roleTypes.${groupedRoleAssignment.roleType}`,
            prettyRoleName(groupedRoleAssignment.roleType)
          )}
        </H3>
        {isCollapsed && !isMobile && renderCollapsedTitle()}
      </div>

      <LabelRegular className="text-foreground_faded">
        {t(`roleAssignment.roleTypesSubtitles.${groupedRoleAssignment.roleType}`)}
      </LabelRegular>

      {groupedRoleAssignment.expiryDate && (
        <LabelRegular className="text-foreground_faded mt-4 mb-1">
          {t('roleAssignment.expirationDate', {
            date: format(parseISO(groupedRoleAssignment.expiryDate), 'dd/MM')
          })}
        </LabelRegular>
      )}
      {showDeleteRoleDialog && (
        <DeleteRoleDialog
          open={showDeleteRoleDialog}
          setOpenModal={setShowDeleteRoleDialog}
          groupedRoleAssignment={groupedRoleAssignment}
          refetchRoles={refetchRoles}
          isUserCreation={isUserCreation}
        />
      )}
    </div>
  )
}
